import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Heart,
  Play,
  Eye,
  ArrowLeft,
  Search,
  Filter,
  ChevronDown,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { VideoCard } from '../model/home';
import { getExporeList } from '../api/expore';
import { Subject } from '../model/video';
import { subjectColors } from '../consts/subject';
import { formatDuration, getSubjectByVideo } from '../utils/VideoUtils';
import PlayBtn from './ui/PlayBtn';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

interface ExplorePageProps {
  userid?: string | null;
  onVideoClick: (videoId: string) => void;
  onBackToMyVideos: () => void;
}

const formatTimeAgo = (date: Date) => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor(diff / (1000 * 60));

  if (days >= 7) {
    // Format as YYYY/MM/DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  }
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
};

export function ExplorePage({
  onVideoClick,
  userid,
  onBackToMyVideos,
}: ExplorePageProps) {
  const navigate = useNavigate();
  const [likedVideos, setLikedVideos] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(8);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef<boolean>(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const lastRef = useRef<string>('');
  const [last, setLast] = useState<string>('');
  const [selectedSubject, setSelectedSubject] =
    useState<Subject>('All Subjects');
  const selectedSubjectRef = useRef<Subject>('All Subjects');
  const sortOrderRef = useRef<'popular' | 'newest' | 'oldest'>('popular');
  const [sortOrder, setSortOrder] = useState<'popular' | 'newest' | 'oldest'>(
    'popular',
  );
  const [videoList, setVideoList] = useState<VideoCard[]>([]);

  // Sort subjects alphabetically
  const sortedSubjects = Object.keys(subjectColors) as Subject[];

  const toggleLike = (videoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLikedVideos((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };
  const fetchList = async (ref?: string) => {
    if (isLoadingRef.current) {
      return;
    }
    setIsLoading(true);
    isLoadingRef.current = true;

    // if (userid) {
    try {
      console.log('isfist:', ref, lastRef.current);
      const res = await getExporeList(
        sortOrderRef.current,
        selectedSubjectRef.current == 'All Subjects'
          ? ''
          : selectedSubjectRef.current,
        ref ? ref : void 0,
      );
      if (ref) {
        setVideoList((prev) => prev.concat(res.cards || []));
      } else {
        setVideoList(res.cards || []);
      }
      console.log(res.ref_id);
      setLast(res.ref_id);
      if (res.ref_id) {
        lastRef.current = res.ref_id;
      }
    } catch (e) {
      toast.error(e.message || 'request fail');
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
    // }
  };
  useEffect(() => {
    (async () => {
      await fetchList('');
      if (lastRef.current) {
        await fetchList(lastRef.current);
      } else {
        setLast('');
      }
      if (lastRef.current) {
        await fetchList(lastRef.current);
      } else {
        setLast('');
      }
    })();
  }, [sortOrder, selectedSubject]);
  const updateList = (e) => {
    try {
      console.log(
        scrollAreaRef.current.scrollTop,
        scrollAreaRef.current.clientHeight,
        scrollAreaRef.current.scrollHeight,
      );
      if (
        scrollAreaRef.current.scrollTop + scrollAreaRef.current.clientHeight >=
        scrollAreaRef.current.scrollHeight
      ) {
        fetchList(lastRef.current);
      }
    } catch (e) {
      toast.error(e.message || 'get history error');
    }
  };
  useEffect(() => {
    setTimeout(() => {
      if (scrollAreaRef.current) {
        scrollAreaRef.current.addEventListener('scroll', updateList);
      }
    }, 50);
    return () => {
      if (scrollAreaRef.current) {
        scrollAreaRef.current.removeEventListener('scroll', updateList);
      }
    };
  }, []);
  const sortMap: Record<'popular' | 'newest' | 'oldest', string> = {
    popular: 'Most Popular',
    oldest: 'Oldest First',
    newest: 'Newest First',
  };
  const handleSort = async (stype: 'popular' | 'newest' | 'oldest') => {
    setSortOrder(stype);
    sortOrderRef.current = stype;
  };

  return (
    <div
      ref={scrollAreaRef}
      style={{ height: '100vh', overflow: 'scroll' }}
      className="w-full"
    >
      <div className="min-h-screen pt-24 pb-12 px-18 bg-white">
        <div className="max-w-[1440px] mx-auto">
          <div className="mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
            <h1 className="mb-2 font-alipuhui-b">Explore Popular Clips</h1>
            <p className="text-muted-foreground">
              Discover and learn from videos created by the community
            </p>
          </div>

          {/* Search and Filter Controls */}
          <div className="flex flex-wrap gap-4 mb-8 justify-between animate-in fade-in slide-in-from-top-4 duration-500 delay-100">
            {/* Left side - Subject Filter */}
            <div className="flex gap-4">
              {isLoading ? (
                <DropdownMenu open={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-xl bg-white">
                      <Filter className="w-4 h-4 mr-2" />
                      {selectedSubject}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {sortedSubjects.map((subject) => (
                      <DropdownMenuItem
                        key={subject}
                        onClick={() => {
                          setSelectedSubject(subject);
                          selectedSubjectRef.current = subject;
                        }}
                      >
                        {subject}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-xl bg-white">
                      <Filter className="w-4 h-4 mr-2" />
                      {selectedSubject}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {sortedSubjects.map((subject) => (
                      <DropdownMenuItem
                        key={subject}
                        onClick={() => {
                          setSelectedSubject(subject);
                          selectedSubjectRef.current = subject;
                        }}
                      >
                        {subject}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Right side - Sort and Search */}
            <div className="flex gap-4">
              {/* Sort Order */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-xl bg-white">
                    {sortMap[sortOrder]}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleSort('popular')}>
                    Most Popular
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('newest')}>
                    Newest First
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('oldest')}>
                    Oldest First
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Search */}
              {/* <div className="relative w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name"
                className="pl-10 bg-white border-border rounded-xl"
              />
            </div> */}
            </div>
          </div>
          {(videoList || []).length === 0 ? (
            <div className="text-center py-16 animate-in fade-in duration-500">
              <div className="mb-4 flex justify-center">
                <img
                  src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-11-10/312521f1-74a7-4d0d-b3e3-dd14dd7f9634/6edd4dcb-3619-4d12-bfa3-34f4ae2144b9.png"
                  alt="No videos"
                  style={{ width: '140px', height: '140px' }}
                />
              </div>
              <h3 className="mb-2 text-black">No videos found</h3>
              <p className="text-muted-foreground">
                {selectedSubject !== 'All Subjects'
                  ? 'Try adjusting your filters'
                  : 'Start generating your first video explanation!'}
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-start gap-4 flex-wrap video-list">
                {(videoList || []).map((video, index) => {
                  // const videoSubject = video.subject || 'Others'
                  // const subjectColor = subjectColors[videoSubject] ?  subjectColors[videoSubject] : subjectColors['Others'];
                  const [videoSubject, subjectColor] = getSubjectByVideo(video);
                  // const isLiked = likedVideos.has(video.id);
                  const isLiked = false;
                  // const hasWatchProgress = video.watchProgress !== undefined;
                  const hasWatchProgress = false;

                  return (
                    <Card
                      key={`${video.video_id}_${index}_${video.video_url}`}
                      onClick={() => onVideoClick(video.video_id)}
                      className="w-[275px] overflow-hidden hover:shadow-2xl transition-all duration-300 shrink-0 rounded-3xl cursor-pointer hover:scale-105"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      {/* Thumbnail */}
                      <div
                        className="relative aspect-video flex items-center justify-center overflow-hidden"
                        style={{
                          // background:
                          //   'linear-gradient(135deg, rgba(255, 231, 92, 0.1) 10%, rgba(255, 231, 92, 0.05) 95%)',
                          background: `url(${video.cover_img || 'https://cdn.jumpstar.app/solve/ai_gen/20251201/9f2c75b4-6e7d-4380-9ca5-c88d066b5c1f.png'}) center center / 100% no-repeat`,
                          borderRadius: '22px 22px 0px 0px',
                        }}
                      >
                        {/* Play button with white background circle */}
                        {/* <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/90 rounded-full shadow-[0px_6px_7.5px_-4.5px_rgba(0,0,0,0.1)] w-12 h-12 flex items-center justify-center pl-1">
                          <Play className="w-5 h-5 text-black fill-black" />
                        </div> */}
                        <PlayBtn />

                        {/* Duration - bottom right */}
                        <div className="absolute bottom-2 right-2">
                          <div className="bg-black/30 text-white text-xs px-2 py-0.5 rounded">
                            {formatDuration(Math.floor(video.video_duration))}
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-3 pt-0.5 flex flex-col h-[140px]">
                        {/* Subject Badge and Creation Time */}
                        <div className="flex items-center justify-between mb-2">
                          <Badge
                            className={`${subjectColor.bg} ${subjectColor.text} rounded-full px-3 py-0.5 border border-black/5`}
                          >
                            {video.subject || 'Others'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatTimeAgo(new Date(video.create_ts * 1000))}
                          </span>
                        </div>

                        {/* Title - always takes 2 lines space */}
                        <h4 className="mb-2 line-clamp-2 min-h-12 h-12">
                          <ReactMarkdown
                            remarkPlugins={[remarkMath]} // 解析数学公式
                            rehypePlugins={[rehypeKatex]} // 渲染数学公式
                          >
                            {video.title}
                          </ReactMarkdown>
                        </h4>

                        {/* Spacer to push button to bottom */}
                        <div className="grow"></div>

                        {/* Ready to play button */}
                        <Button
                          className="w-full rounded-2xl flex items-center justify-start gap-2 bg-[#302f2b] hover:bg-[#302f2b]/90 text-[#ffe75c] h-9"
                          onClick={(e) => {
                            e.stopPropagation();
                            onVideoClick(video.video_id);
                          }}
                        >
                          <Play className="w-4 h-4" />
                          <span>Ready to play</span>
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
              {last && (
                <div className="text-center mt-6 animate-in fade-in duration-500">
                  <Button
                    onClick={() => fetchList(lastRef.current)}
                    variant="outline"
                    className="rounded-xl"
                  >
                    Load More
                  </Button>
                </div>
              )}
            </>
          )}

          <button
            onClick={() => navigate('/')}
            className="fixed bottom-8 right-8 bg-[#ffe75c] text-black px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
